import React, { useState, memo } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { FileUploadPreview } from '../../components/FileUploadPreview';
import { MissingOrInvalidFiles } from '../../components/MissingOrInvalidFiles';
import { Upload, Button, Typography, Card } from '../../../../../components';
import { validateZip, validateExcel, validateJson } from '../../utils/validation';
import { getMaxCompressedFileSizeMB, getMaxUncompressedFileSizeMB } from '../../utils/uploadLimits';
import { getBytesfromMB } from '../../../../../lib/helpers';
import { REQUIRED_CSV_FILES, REQUIRED_EXCEL_SHEETS, IMPORT_TYPES } from '../../../../../constants/discovery';
import { useNotificationContext } from '../../../../../providers';
import { useUploadDiscoveryImportFileMutation } from '../../../../../api';

const { Paragraph, Title, Text } = Typography;
const { Dragger } = Upload;

export const SelectSupportingFile = memo(({
    resetForm,
    form,
    analysisFileList,
    fileList,
    setFileList,
    handleDelete,
    orgId,
    isUploading,
    setIsUploading,
    setStep,
    setActiveImportId,
}) => {
    const [validationError, setValidationError] = useState(null);
    const { notifyError } = useNotificationContext();
    const [uploadDiscoveryImportFile] = useUploadDiscoveryImportFileMutation();
    const maxCompressedSize = getMaxCompressedFileSizeMB();
    const maxUncompressedSize = getMaxUncompressedFileSizeMB();

    const uploadProps = {
        name: 'awsTransformSupportingUpload',
        multiple: false,
        accept: '.zip, .xlsx, .json',
        beforeUpload: async (file) => {
            const maxSizeInBytes = getBytesfromMB(maxCompressedSize);
            if (file.size > maxSizeInBytes) {
                setValidationError({
                    message: (
                        <>
                            <Title level={2}>File size error</Title>
                            <Paragraph>File size exceeds the maximum allowed size of {maxCompressedSize} MB.</Paragraph>
                        </>
                    ),
                });
                return Upload.LIST_IGNORE; // Prevents file from being used
            }
            const isJson = file.name.endsWith('.json');
            const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
            const isValid = isJson
                ? await validateJson(file, setValidationError)
                : isExcel
                  ? await validateExcel(file, REQUIRED_EXCEL_SHEETS, setValidationError)
                  : await validateZip(file, maxUncompressedSize, REQUIRED_CSV_FILES, setValidationError);
            if (!isValid) {
                return Upload.LIST_IGNORE; // Prevents file from being used
            }
            return false; // Prevents automatic upload; we upload explicitly on Continue
        },
        fileList: fileList,
        onChange(info) {
            setFileList(info.fileList);
        },
    };

    // Uploads the analysis file selected in step 1 and this supporting file together in a single request.
    const submitFiles = async () => {
        setIsUploading(true);
        try {
            const importName = form.getFieldValue('importName');
            const result = await uploadDiscoveryImportFile({
                file: fileList[0]?.originFileObj,
                analysisFile: analysisFileList[0]?.originFileObj,
                orgId,
                importName,
                toolId: IMPORT_TYPES.awsTransform,
            }).unwrap();
            setActiveImportId(result?.id ?? null);
            setStep(4);
        } catch (error) {
            if (error?.status === 415) {
                notifyError({
                    message: 'Unsupported File Type',
                    description: 'The file format is not supported. Please upload a valid ZIP, XLSX, or JSON file.',
                    duration: 10,
                });
            } else {
                notifyError({
                    message: 'Upload Failed',
                    description: error?.data?.message || 'An error occurred while uploading the files.',
                    duration: 10,
                });
            }
            resetForm();
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            {validationError ? (
                <MissingOrInvalidFiles error={validationError} resetForm={resetForm} />
            ) : (
                <>
                    <Card className="upload-card">
                        <Title level={2}>Analysis file</Title>
                        <FileUploadPreview file={analysisFileList[0]} />
                    </Card>
                    <Card className="upload-card">
                        <Title level={2}>Upload supporting files</Title>
                        <Paragraph>
                            Select either your RVTools export (zip or xlsx) <strong>OR</strong> your AWS Discovery
                            appliance JSON export (vmware_data_full.json)
                        </Paragraph>
                        {fileList.length === 0 ? (
                            <Dragger className="custom-upload" {...uploadProps}>
                                <div className="ant-upload-drag-icon">
                                    <InboxOutlined className="upload-icon" />
                                </div>
                                <Text strong>Click or drag file to this area to upload</Text>
                                <Paragraph className="text-light">
                                    Support for an RVTools export (.zip or .xlsx) or a Discovery appliance
                                    vmware_data_full.json ({maxCompressedSize}MB max)
                                </Paragraph>
                            </Dragger>
                        ) : (
                            <FileUploadPreview file={fileList[0]} handleDelete={handleDelete} />
                        )}
                    </Card>
                    <div className="button-group margin-top-10">
                        <Button type="default" size="medium" disabled={isUploading} onClick={resetForm}>
                            Back
                        </Button>
                        <Button
                            onClick={submitFiles}
                            type="primary"
                            size="medium"
                            disabled={fileList.length === 0}
                            loading={isUploading}
                        >
                            {isUploading ? 'Adding to Inventory...' : 'Add to Inventory'}
                        </Button>
                    </div>
                </>
            )}
        </>
    );
});
